import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Shop from "@/pages/Shop";
import Games from "@/pages/Games";
import GameDetail from "@/pages/GameDetail";
import Auth from "@/pages/Auth";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Enroll from "@/pages/Enroll";
import Registration from "@/pages/Registration";
import AgendasLibrary from "@/pages/agendas/AgendasLibrary";
import AgendaTemplateView from "@/pages/agendas/AgendaTemplateView";
import AgendaBuild from "@/pages/agendas/AgendaBuild";
import ParticipantPlay from "@/pages/ParticipantPlay";

import DirectorDashboard from "@/pages/director/Dashboard";
import AgendaBrowser from "@/pages/director/AgendaBrowser";
import AgendaView from "@/pages/director/AgendaView";
import ClassRunner from "@/pages/director/ClassRunner";
import PromptGenerator from "@/pages/director/PromptGenerator";
import Roster from "@/pages/director/Roster";
import Frankenstein from "@/pages/director/Frankenstein";
import ParticipantPortal from "@/pages/participant/Portal";
import Admin from "@/pages/admin/Admin";
import CertifiedDirectors from "@/pages/CertifiedDirectors";
import Team from "@/pages/Team";
import Classes from "@/pages/Classes";
import MyRosters from "@/pages/director/MyRosters";
import Toolbox from "@/pages/director/Toolbox";
import Calendar from "@/pages/Calendar";

const Shell = ({ children }) => (
  <div className="App flex flex-col min-h-screen">
    <a href="#main" className="skip-link">Skip to main content</a>
    <Navbar />
    <main id="main" className="flex-1">{children}</main>
    <Footer />
  </div>
);

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Shell><Home /></Shell>} />
            <Route path="/our-story" element={<Navigate to="/about" replace />} />
            <Route path="/about" element={<Shell><About /></Shell>} />
            <Route path="/impact" element={<Navigate to="/about" replace />} />
            <Route path="/journal" element={<Navigate to="/about" replace />} />
            <Route path="/contact" element={<Shell><Contact /></Shell>} />
            <Route path="/shop" element={<Shell><Shop /></Shell>} />
            <Route path="/classes" element={<Shell><Classes /></Shell>} />
            <Route path="/calendar" element={<Shell><Calendar /></Shell>} />
            <Route path="/certified-directors" element={<Shell><CertifiedDirectors /></Shell>} />
            <Route path="/team" element={<Shell><Team /></Shell>} />
            <Route path="/enroll/event/:eventId" element={<Shell><Enroll /></Shell>} />
            <Route path="/enroll/:lookupKey" element={<Shell><Enroll /></Shell>} />
            <Route path="/registration" element={<Shell><Registration /></Shell>} />
            <Route path="/agendas/library" element={<ProtectedRoute roles={["admin", "director", "participant", "caregiver", "staff"]}><Shell><AgendasLibrary /></Shell></ProtectedRoute>} />
            <Route path="/agendas/library/:tid" element={<ProtectedRoute roles={["admin", "director", "participant", "caregiver", "staff"]}><Shell><AgendaTemplateView /></Shell></ProtectedRoute>} />
            <Route path="/agendas/build" element={<ProtectedRoute roles={["admin", "director", "participant", "caregiver", "staff"]}><Shell><AgendaBuild /></Shell></ProtectedRoute>} />
            <Route path="/games" element={<ProtectedRoute roles={["admin", "director", "participant", "caregiver", "staff"]}><Shell><Games /></Shell></ProtectedRoute>} />
            <Route path="/play" element={<Shell><ParticipantPlay /></Shell>} />
            <Route path="/play/:code" element={<Shell><ParticipantPlay /></Shell>} />
            <Route path="/games/:gameId" element={<ProtectedRoute roles={["admin", "director", "participant", "caregiver", "staff"]}><Shell><GameDetail /></Shell></ProtectedRoute>} />
            <Route path="/login" element={<Shell><Auth /></Shell>} />
            <Route path="/payment/success" element={<Shell><PaymentSuccess /></Shell>} />

            <Route path="/director" element={<ProtectedRoute roles={["director", "admin"]}><Shell><DirectorDashboard /></Shell></ProtectedRoute>} />
            <Route path="/director/agenda" element={<ProtectedRoute roles={["director", "admin"]}><Shell><AgendaBrowser /></Shell></ProtectedRoute>} />
            <Route path="/director/agenda/:classId" element={<ProtectedRoute roles={["director", "admin"]}><Shell><AgendaView /></Shell></ProtectedRoute>} />
            <Route path="/director/generator" element={<ProtectedRoute roles={["director", "admin"]}><Shell><PromptGenerator /></Shell></ProtectedRoute>} />
            <Route path="/director/frankenstein" element={<ProtectedRoute roles={["director", "admin"]}><Shell><Frankenstein /></Shell></ProtectedRoute>} />
            <Route path="/director/roster/:classId" element={<ProtectedRoute roles={["director", "admin"]}><Shell><Roster /></Shell></ProtectedRoute>} />
            <Route path="/director/rosters" element={<ProtectedRoute roles={["director", "admin"]}><Shell><MyRosters /></Shell></ProtectedRoute>} />
            <Route path="/director/toolbox" element={<ProtectedRoute roles={["director", "admin"]}><Shell><Toolbox /></Shell></ProtectedRoute>} />
            {/* Class Runner is fullscreen — no Shell */}
            <Route path="/director/run/:classId" element={<ProtectedRoute roles={["director", "admin"]}><ClassRunner /></ProtectedRoute>} />

            <Route path="/participant" element={<ProtectedRoute roles={["participant", "caregiver", "admin"]}><Shell><ParticipantPortal /></Shell></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Shell><Admin /></Shell></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
